import {
  forwardRef,
  type PropsWithChildren,
  type ReactNode,
  useState,
} from 'react'
import type {
  ButtonProps as AriaButtonProps,
  PressEvent,
} from 'react-aria-components'
import { Button as AriaButton } from 'react-aria-components'
import { tv, type VariantProps } from 'tailwind-variants'
import FluentSpinnerIos20Filled from '~icons/fluent/spinner-ios-20-filled'

export const buttonStyles = tv({
  slots: {
    root: 'inline-grid grid-cols-[auto_1fr_auto] items-center h-[--ui-size] rounded-md focus-visible:outline outline-2 outline-offset-1 transition duration-75',
    label: 'row-start-1 col-start-2 whitespace-nowrap',
    startIcon: 'row-start-1 col-start-1 size-[--ui-size]',
    endIcon: 'row-start-1 col-start-3 size-[--ui-size]',
    spinner: 'place-self-center animate-spin',
  },
  variants: {
    variant: {
      outlined: {
        root: [
          'border',
          // Light
          'disabled:text-black/40 disabled:border-black/20',
          // Dark
          'dark:disabled:text-white/40 dark:disabled:border-white/20',
        ],
      },
      filled: {
        root: [
          // Light
          'disabled:text-black/50 disabled:bg-black/15',
          // Dark
          'dark:disabled:text-white/50 dark:disabled:bg-white/15',
        ],
      },
      ghost: {
        root: [
          // Light
          'disabled:text-black/40',
          // Dark
          'dark:disabled:text-white/40',
        ],
      },
      unstyled: {},
    },
    size: {
      small: {
        spinner: 'size-5',
      },
      normal: {
        spinner: 'size-5',
      },
      large: {
        spinner: 'size-6',
      },
      xl: {
        spinner: 'size-7',
      },
    },
    color: {
      normal: {},
      danger: {},
      admin: {},
      theme: {},
      /** @todo Should remove shadows, and rename it to gray afterwards */
      'grayish-sidebar': {},
    },
    /**
     * Enforce colorscheme
     * No effect unless given color has implemented different colorScheme styles
     */
    colorScheme: {
      auto: {},
      dark: {},
      light: {},
    },
    hasLabel: {
      false: {
        label: 'hidden',
      },
    },
    hasStartIcon: {
      false: {
        startIcon: 'hidden',
      },
    },
    hasEndIcon: {
      false: {
        endIcon: 'hidden',
      },
    },
    hasBusyLabel: {
      true: {},
      false: {},
    },
    busy: {
      false: {
        spinner: 'hidden',
      },
    },
  },
  compoundVariants: [
    // Text sizes
    {
      variant: ['outlined', 'filled'],
      size: ['small', 'normal'],
      className: {
        root: 'text-sm',
      },
    },
    {
      variant: ['outlined', 'filled'],
      size: 'large',
      className: {
        root: 'text-base',
      },
    },
    {
      variant: ['outlined', 'filled'],
      size: 'xl',
      className: {
        root: 'text-lg',
      },
    },
    // Sizes for non-ghost variants
    {
      variant: ['outlined', 'filled'],
      size: 'small',
      className: {
        root: '[--ui-size:theme(spacing.7)]',
      },
    },
    {
      variant: ['outlined', 'filled'],
      size: 'normal',
      className: {
        root: '[--ui-size:theme(spacing.8)]',
      },
    },
    {
      variant: ['outlined', 'filled'],
      size: 'large',
      className: {
        root: '[--ui-size:theme(spacing.10)]',
      },
    },
    {
      variant: ['outlined', 'filled'],
      size: 'xl',
      className: {
        root: '[--ui-size:theme(spacing.12)]',
      },
    },
    {
      variant: ['outlined', 'filled'],
      hasStartIcon: true,
      className: {
        startIcon: 'grid place-content-center',
      },
    },
    {
      variant: ['outlined', 'filled'],
      hasEndIcon: true,
      className: {
        endIcon: 'grid place-content-center',
      },
    },
    // Paddings with different sizes
    {
      variant: ['outlined', 'filled'],
      hasStartIcon: false,
      size: 'small',
      className: { root: 'pl-2' },
    },
    {
      variant: ['outlined', 'filled'],
      hasLabel: true,
      hasEndIcon: false,
      size: 'small',
      className: { root: 'pr-2' },
    },
    {
      variant: ['outlined', 'filled'],
      hasStartIcon: false,
      size: 'normal',
      className: { root: 'pl-2.5' },
    },
    {
      variant: ['outlined', 'filled'],
      hasLabel: true,
      hasEndIcon: false,
      size: 'normal',
      className: { root: 'pr-2.5' },
    },
    {
      variant: ['outlined', 'filled'],
      hasStartIcon: false,
      size: 'large',
      className: { root: 'pl-3' },
    },
    {
      variant: ['outlined', 'filled'],
      hasLabel: true,
      hasEndIcon: false,
      size: 'large',
      className: { root: 'pr-3' },
    },
    {
      variant: ['outlined', 'filled'],
      hasStartIcon: false,
      size: 'xl',
      className: { root: 'pl-4' },
    },
    {
      variant: ['outlined', 'filled'],
      hasLabel: true,
      hasEndIcon: false,
      size: 'xl',
      className: { root: 'pr-4' },
    },
    // Outlined colors
    {
      variant: 'outlined',
      color: 'normal',
      className: {
        root: [
          // Light
          'hover:bg-black/5 border-black/50 focus-visible:border-black/20 outline-black/80',
          // Dark
          'dark:hover:bg-white/5 dark:border-white/50 dark:focus-visible:border-white/20 dark:outline-white/80',
        ],
      },
    },
    {
      variant: 'outlined',
      color: 'theme',
      className: {
        root: [
          // Light
          'text-purple-600 hover:bg-purple-600/10 border-purple-600 focus-visible:border-purple-600/20 outline-purple-600',
          // Dark
          'dark:text-purple-500 dark:hover:bg-purple-500/10 dark:border-purple-500 dark:focus-visible:border-purple-500/20 dark:outline-purple-500',
        ],
      },
    },
    {
      variant: 'outlined',
      color: 'danger',
      className: {
        root: [
          // Light
          'text-red-600 hover:bg-red-600/10 border-red-600 focus-visible:border-red-600/20 outline-red-600',
          // Dark
          'dark:text-red-500 dark:hover:bg-red-500/10 dark:border-red-500 dark:focus-visible:border-red-500/20 dark:outline-red-500',
        ],
      },
    },
    {
      variant: 'outlined',
      color: 'admin',
      className: {
        root: [
          // Light
          'text-amber-700 hover:bg-amber-600/10 border-amber-600 focus-visible:border-amber-600/20 outline-amber-600',
          // Dark
          'dark:text-amber-500 dark:hover:bg-amber-500/10 dark:border-amber-500 dark:focus-visible:border-amber-500/20 dark:outline-amber-500',
        ],
      },
    },
    // Filled colors
    {
      variant: 'filled',
      color: 'normal',
      colorScheme: 'auto',
      className: {
        root: [
          // Light
          'text-white bg-black hover:bg-[color-mix(in_srgb,#000,#FFF_25%)] outline-black',
          // Dark
          'dark:text-black dark:bg-[color-mix(in_srgb,#FFF,#000_10%)] dark:hover:bg-white dark:outline-white',
        ],
      },
    },
    {
      // Copied dark colors from above
      variant: 'filled',
      color: 'normal',
      colorScheme: 'dark',
      className: {
        root: 'text-black bg-[color-mix(in_srgb,#FFF,#000_10%)] hover:bg-white outline-white',
      },
    },
    {
      variant: 'filled',
      color: 'theme',
      className: {
        root: [
          'text-white bg-purple-600 hover:bg-purple-500 ',
          // Light
          'outline-purple-600',
          // Dark
          'dark:outline-purple-500',
        ],
      },
    },
    {
      variant: 'filled',
      color: 'grayish-sidebar',
      className: {
        root: [
          // Mimic styles from UIButton
          'font-medium h-9',
          'drop-shadow-sm dark:shadow-black/30 hover:shadow-black/20 dark:hover:shadow-black/60 active:shadow-black/10 dark:active:shadow-black shadow hover:shadow-md active:shadow-md dark:active:shadow disabled:shadow-none',
          'text-black bg-background-light',
          // Light
          'ring-black/10 dark:ring-black/20 active:ring-black/20 dark:active:ring-black/40',
          // Dark
          'dark:text-white disabled:text-black/50 dark:disabled:text-white/40',
        ],
      },
    },
    {
      variant: 'filled',
      color: 'danger',
      className: {
        root: 'text-white bg-red-600 hover:bg-red-500 outline-red-500',
      },
    },
    {
      variant: 'filled',
      color: 'admin',
      className: {
        root: [
          'text-white outline-amber-600',
          // Light
          'bg-[color-mix(in_srgb,theme(colors.amber.700),#FFF_20%)] hover:bg-[color-mix(in_srgb,theme(colors.amber.700),#FFF_30%)]',
          // Dark
          'dark:bg-[color-mix(in_srgb,theme(colors.amber.600),#000_20%)] dark:hover:bg-[color-mix(in_srgb,theme(colors.amber.600),#000_10%)]',
        ],
      },
    },
    // Ghost colors
    {
      variant: 'ghost',
      color: 'normal',
      className: {
        root: [
          // Light
          'hover:bg-black/5 outline-black/80',
          // Dark
          'dark:hover:bg-white/5 dark:outline-white/80',
        ],
      },
    },
    {
      variant: 'ghost',
      color: 'danger',
      className: {
        root: [
          // Light
          'text-red-600 hover:bg-red-600/10 outline-red-600',
          // Dark
          'dark:text-red-500 dark:hover:bg-red-500/10 dark:outline-red-500',
        ],
      },
    },
    {
      variant: 'ghost',
      color: 'admin',
      className: {
        root: [
          // Light
          'text-amber-700 hover:bg-amber-600/10 outline-amber-600',
          // Dark
          'dark:text-amber-500 dark:hover:bg-amber-500/10 dark:outline-amber-500',
        ],
      },
    },
    // Spinner positions
    {
      hasStartIcon: true,
      busy: true,
      className: {
        startIcon: 'invisible',
        spinner: 'row-start-1 col-start-1',
      },
    },
    {
      hasStartIcon: false,
      hasEndIcon: true,
      busy: true,
      className: {
        endIcon: 'invisible',
        spinner: 'row-start-1 col-start-3',
      },
    },
    {
      hasStartIcon: false,
      hasEndIcon: false,
      hasBusyLabel: true,
      busy: true,
      className: {
        spinner: 'row-start-1 col-start-3',
      },
    },
    {
      hasStartIcon: false,
      hasEndIcon: false,
      hasBusyLabel: false,
      busy: true,
      className: {
        label: 'invisible',
        spinner: 'row-start-1 col-start-2',
      },
    },
  ],
  compoundSlots: [
    {
      slots: ['startIcon', 'endIcon'],
      variant: ['outlined', 'filled'],
      className: 'h-full aspect-square',
    },
  ],
  defaultVariants: {
    variant: 'outlined',
    size: 'normal',
    color: 'normal',
    hasLabel: false,
    hasStartIcon: false,
    hasEndIcon: false,
  },
})

export interface UIButtonProps
  extends
    Omit<AriaButtonProps, 'isDisabled' | 'className' | 'style' | 'children'>,
    PropsWithChildren,
    StyleProps,
    Pick<
      VariantProps<typeof buttonStyles>,
      'variant' | 'size' | 'color' | 'colorScheme'
    > {
  disabled?: boolean
  busy?: boolean | 'auto'
  busyLabel?: ReactNode
  icon?: ReactNode
  endIcon?: ReactNode

  startIconClassName?: string
  labelClassName?: string
  endIconClassName?: string
}

export const UIButton = forwardRef<HTMLButtonElement, UIButtonProps>(
  function UIButton(props, ref) {
    const [busy, setBusy] = useState(props.busy === true)

    if (typeof props.busy === 'boolean' && busy !== props.busy) {
      setBusy(props.busy)
    }

    async function onPress(ev: PressEvent) {
      if (props.busy === 'auto') {
        try {
          setBusy(true)
          await props.onPress?.(ev)
        } finally {
          setBusy(false)
        }
      } else {
        props.onPress?.(ev)
      }
    }

    const styles = buttonStyles({
      variant: props.variant,
      size: props.size,
      color: props.color,
      colorScheme: props.colorScheme ?? 'auto',
      hasLabel: Boolean(props.children),
      hasStartIcon: Boolean(props.icon),
      hasEndIcon: Boolean(props.endIcon),
      hasBusyLabel: Boolean(props.busyLabel),
      busy,
    })

    return (
      <AriaButton
        ref={ref}
        {...props}
        isDisabled={props.disabled || busy}
        className={styles.root({ className: props.className })}
        style={props.style}
        onPress={onPress}
        onFocus={props.onFocus}
      >
        <span
          className={styles.startIcon({ className: props.startIconClassName })}
        >
          {props.icon}
        </span>
        <span className={styles.label({ className: props.labelClassName })}>
          {busy ? (props.busyLabel ?? props.children) : props.children}
        </span>
        <span className={styles.endIcon({ className: props.endIconClassName })}>
          {props.endIcon}
        </span>
        <FluentSpinnerIos20Filled className={styles.spinner()} />
      </AriaButton>
    )
  },
)
UIButton.displayName = 'UIButton'
